import {
  Button,
  Card,
  FieldError,
  Form as HeroForm,
  Input,
  Label,
  Modal,
  Table,
  useOverlayState,
} from '@heroui/react'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useForm } from '@tanstack/react-form'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Edit2, MapPin, Plus, Trash2 } from 'lucide-react'
import * as React from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { useHandleError } from '~/hooks/use-handle-error'
import { authApiClient } from '~/lib/api-client'
import { DeleteRegionModal } from './-components/delete-region-modal'
import { useDebounce } from 'use-debounce'

const regionSchema = z.object({
  name: z.string().min(1, '名称为必填项'),
  sourceId: z.string().min(1, 'Source ID 为必填项'),
  parentId: z.string().optional().nullable(),
})

type RegionFormValues = z.infer<typeof regionSchema>

export const Route = createFileRoute('/admin/regions')({
  component: RegionsPage,
})

function RegionsPage() {
  const queryClient = useQueryClient()
  const handleError = useHandleError()
  const modalState = useOverlayState()
  const [editingId, setEditingId] = React.useState<number | null>(null)
  const [deletingRegion, setDeletingRegion] = React.useState<{ id: number; name: string } | null>(null)
  
  const [localSearch, setLocalSearch] = React.useState('')
  const [debouncedSearch] = useDebounce(localSearch, 300)

  const { data: regions } = useSuspenseQuery({
    queryKey: ['admin', 'regions'],
    queryFn: async () => {
      const res = await authApiClient.regions.$get()
      if (!res.ok) throw res
      return await res.json()
    },
  })

  const mutation = useMutation({
    mutationFn: async (values: RegionFormValues) => {
      const res = editingId
        ? await authApiClient.regions[':id'].$patch({ param: { id: String(editingId) }, json: values as any, header: { Authorization: '' } })
        : await authApiClient.regions.$post({ json: values as any, header: { Authorization: '' } })
      if (!res.ok) throw res
      return res.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'regions'] })
      toast.success(editingId ? '区域更新成功' : '区域创建成功')
      modalState.close()
      form.reset()
      setEditingId(null)
    },
    onError: handleError,
  })

  const form = useForm({
    defaultValues: {
      name: '',
      sourceId: '',
      parentId: '',
    } as RegionFormValues,
    onSubmit: async ({ value }) => {
      mutation.mutate(value)
    },
  })

  const handleEdit = React.useCallback((region: any) => {
    setEditingId(region.id)
    form.reset()
    form.setFieldValue('name', region.name || '')
    form.setFieldValue('sourceId', region.sourceId || '')
    form.setFieldValue('parentId', region.parentId || '')
    modalState.open()
  }, [form, modalState])

  const handleAddNew = () => {
    setEditingId(null)
    form.reset()
    modalState.open()
  }

  const columns = React.useMemo(() => {
    const columnHelper = createColumnHelper<any>()
    return [
    columnHelper.accessor('name', {
      header: '区域名称',
      cell: info => (
        <div className="flex items-center gap-2">
          <MapPin className="size-4 text-primary/60" />
          <span className="font-bold">{info.getValue()}</span>
        </div>
      )
    }),
    columnHelper.accessor('sourceId', {
      header: '数据源 ID',
      cell: info => <code className="text-xs bg-muted px-1 rounded">{info.getValue()}</code>
    }),
    columnHelper.accessor('parentId', {
      header: '父级 ID',
      cell: info => info.getValue() 
        ? <code className="text-xs text-muted">{info.getValue()}</code> 
        : <span className="text-xs italic text-muted-foreground">根区域 (Root)</span>
    }),
    columnHelper.display({
      id: 'actions',
      header: () => <div className="text-right">操作</div>,
      cell: info => (
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" isIconOnly onPress={() => handleEdit(info.row.original)}>
            <Edit2 className="size-4" />
          </Button>
          <Button variant="danger-soft" size="sm" isIconOnly aria-label={`删除 ${info.row.original.name}`} onPress={() => setDeletingRegion({ id: info.row.original.id, name: info.row.original.name })}>
            <Trash2 className="size-4" />
          </Button>
        </div>
      )
    })
  ]
  }, [handleEdit])

  const filteredRegions = React.useMemo(() => {
    if (!regions) return []
    if (!debouncedSearch) return regions
    
    const searchLower = debouncedSearch.toLowerCase()
    return regions.filter(r => 
      (r.name && r.name.toLowerCase().includes(searchLower)) ||
      (r.cn && r.cn.toLowerCase().includes(searchLower)) ||
      (r.sourceId && r.sourceId.toLowerCase().includes(searchLower))
    )
  }, [regions, debouncedSearch])

  const table = useReactTable({
    data: filteredRegions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">区域管理</h1>
          <p className="text-muted mt-1 font-medium">管理地理区域和行政级别划分。</p>
        </div>
        <div className="flex items-center gap-3">
          <Input
            placeholder="搜索区域名称或源 ID..."
            className="w-full md:w-64"
            value={localSearch}
            onChange={e => setLocalSearch(e.target.value)}
          />
          <Button variant="primary" onPress={handleAddNew}>
            <Plus className="size-4 mr-2" />
            {' '}
            添加区域
          </Button>
        </div>
      </div>

      <Card variant="secondary">
        <Table>
          <Table.ScrollContainer>
            <Table.Content aria-label="Regions table">
              <Table.Header>
                {table.getHeaderGroups().map(headerGroup => (
                  <React.Fragment key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <Table.Column key={header.id} isRowHeader={header.column.id === 'name'}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </Table.Column>
                    ))}
                  </React.Fragment>
                ))}
              </Table.Header>
              <Table.Body>
                {table.getRowModel().rows.map(row => (
                  <Table.Row key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <Table.Cell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      </Card>

      <Modal>
        <Modal.Backdrop isOpen={modalState.isOpen} onOpenChange={open => !open && modalState.close()} variant="blur">
          <Modal.Container size="md">
            <Modal.Dialog>
              <Modal.CloseTrigger />
              <Modal.Header><Modal.Heading>{editingId ? '编辑区域' : '添加区域'}</Modal.Heading></Modal.Header>
              <Modal.Body>
                <HeroForm className="flex flex-col gap-4" onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  form.handleSubmit();
                }}>
                  <form.Field
                    name="name"
                    validators={{ onChange: regionSchema.shape.name }}
                    children={(field) => (
                      <div className="flex flex-col gap-1">
                        <Label>区域名称 (Region Name)</Label>
                        <Input 
                          name={field.name} 
                          value={field.state.value} 
                          onBlur={field.handleBlur} 
                          onChange={(e) => field.handleChange(e.target.value)} 
                          placeholder="e.g. Tokyo" 
                          data-invalid={field.state.meta.errors.length > 0} 
                        />
                        {field.state.meta.errors.length > 0 && <FieldError>{field.state.meta.errors.join(', ')}</FieldError>}
                      </div>
                    )}
                  />
                  
                  <form.Field
                    name="sourceId"
                    validators={{ onChange: regionSchema.shape.sourceId }}
                    children={(field) => (
                      <div className="flex flex-col gap-1">
                        <Label>数据源 ID (Source ID)</Label>
                        <Input 
                          name={field.name} 
                          value={field.state.value} 
                          onBlur={field.handleBlur} 
                          onChange={(e) => field.handleChange(e.target.value)} 
                          placeholder="e.g. tokyo_01" 
                          data-invalid={field.state.meta.errors.length > 0} 
                        />
                        {field.state.meta.errors.length > 0 && <FieldError>{field.state.meta.errors.join(', ')}</FieldError>}
                      </div>
                    )}
                  />

                  <form.Field
                    name="parentId"
                    validators={{ onChange: regionSchema.shape.parentId }}
                    children={(field) => (
                      <div className="flex flex-col gap-1">
                        <Label>父级区域 ID (可选)</Label>
                        <Input 
                          name={field.name} 
                          value={field.state.value || ''} 
                          onBlur={field.handleBlur} 
                          onChange={(e) => field.handleChange(e.target.value)} 
                          placeholder="留空则作为根区域" 
                        />
                      </div>
                    )}
                  />
                  
                  <div className="flex justify-end gap-3 mt-4">
                    <Button variant="ghost" onPress={() => modalState.close()}>取消</Button>
                    <form.Subscribe
                      selector={(state) => [state.isSubmitting]}
                      children={([isSubmitting]) => (
                        <Button variant="primary" type="submit" isDisabled={isSubmitting}>
                          {editingId ? '保存更改' : '创建区域'}
                        </Button>
                      )}
                    />
                  </div>
                </HeroForm>
              </Modal.Body>
            </Modal.Dialog>
          </Modal.Container>
        </Modal.Backdrop>
      </Modal>

      <DeleteRegionModal
        isOpen={!!deletingRegion}
        onClose={() => setDeletingRegion(null)}
        region={deletingRegion}
      />
    </div>
  )
}

